import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { get, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { NODE_TYPES } from "utils/constants";
import AutomationsTab from "./AutomationsTab";
import NextSteps from "./NextSteps";
import NodeConfigPanel from "./NodeConfigPanel";
import QuestionsSection from "./QuestionsSection";
import ScreenTab from "./ScreenTab";
import SettingsTab from "./SettingsTab";
import { FieldsSlotContext, StatesSlotContext } from "./states-slot";
import { SCREEN_STATES, screenState } from "./step-summary";
import {
  barTabs,
  screenVersions,
  shownTab,
  STEP_KINDS,
  STEP_TABS,
  stepKind,
} from "./step-tabs";
import WaitTab from "./WaitTab";

const SCREEN_TABS = [STEP_TABS.MAIN, STEP_TABS.FIELDS];

export const SIDE_TABS = [STEP_TABS.NEXT, STEP_TABS.SETTINGS];

const STATUS_TONES = {
  OK: "ok",
  DRAFT: "draft",
  ERROR: "error",
  MUTED: "muted",
};

const StatusPanel = ({
  node,
  nodes = [],
  edges = [],
  focusedEdgeId = null,
  activeTab = STEP_TABS.MAIN,
  onTabChange = () => {},
  onEdgesChange,
  persisted,
  processId,
  variables = [],
  liveFormDefinition,
  onChange,
  isStart = false,
  onStartHere,
  onDelete,
  reload,
  sideBody = null,
  sideTab = STEP_TABS.NEXT,
  onSideTab = () => {},
  sideOpen = false,
  onSideOpen = () => {},
  sideMeta = null,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });
  const NEXT = t("CANVAS_EDITOR.NEXT", { returnObjects: true });
  const STEP = t("CANVAS_EDITOR.STEP", { returnObjects: true });
  const WORKSPACE = t("CANVAS_EDITOR.WORKSPACE", { returnObjects: true });
  const UNNAMED = t("CANVAS_EDITOR.STEPS.UNNAMED");
  const TAGS = t("PROCESS_TAGS", { returnObjects: true }) || {};
  const [statesSlot, setStatesSlot] = useState(null);
  const [fieldsPanel, setFieldsPanel] = useState(null);
  const [fieldsBadge, setFieldsBadge] = useState(null);
  const fieldsSlot = useMemo(
    () => ({ panel: fieldsPanel, badge: fieldsBadge }),
    [fieldsPanel, fieldsBadge],
  );

  if (isNil(node)) return null;

  const data = node.data || {};
  const nodeType = data.nodeType || NODE_TYPES.STATUS;
  const kind = stepKind(nodeType);
  const isScreen = kind === STEP_KINDS.SCREEN;
  const requested = shownTab(nodeType, activeTab);
  const middleTabs = barTabs(nodeType).filter(
    (key) => !SIDE_TABS.includes(key),
  );
  const shown = middleTabs.includes(requested)
    ? requested
    : middleTabs[0] || null;
  const sideTabs = [
    ...(isScreen ? [STEP_TABS.FIELDS] : []),
    ...barTabs(nodeType).filter((key) => SIDE_TABS.includes(key)),
  ];
  const sideShown = sideTabs.includes(shownTab(nodeType, sideTab))
    ? shownTab(nodeType, sideTab)
    : sideTabs[0];
  const fieldsOpen = isScreen && sideOpen && sideShown === STEP_TABS.FIELDS;

  const mainTab = () => {
    if (kind === STEP_KINDS.FORM)
      return (
        <div className="canvas-tab">
          <QuestionsSection
            formDefinition={data.formDefinition}
            variables={variables}
            title={STEP.QUESTIONS.FORM_TITLE}
            hint={STEP.QUESTIONS.FORM_HINT}
            onChange={(next) => onChange("formDefinition", next)}
          />
        </div>
      );
    if (kind === STEP_KINDS.WAIT)
      return (
        <WaitTab
          node={node}
          onChange={onChange}
          onShowNext={() => onTabChange(STEP_TABS.NEXT)}
        />
      );
    return (
      <div className="canvas-tab">
        <p className="canvas-tab__hint">{STEP.ACTION_HINT}</p>
        <NodeConfigPanel
          key={node.id}
          nodeType={nodeType}
          config={data.config || {}}
          labels={FORM.CONFIG || {}}
          failureLabels={STEP.AUTOMATIONS.FAILURE}
          onConfigChange={(next) => onChange("config", next)}
        />
      </div>
    );
  };

  const tabContent = {
    [STEP_TABS.MAIN]: mainTab,
    [STEP_TABS.NEXT]: () => (
      <NextSteps
        node={node}
        nodes={nodes}
        edges={edges}
        variables={variables}
        focusedEdgeId={focusedEdgeId}
        onEdgesChange={onEdgesChange}
      />
    ),
    [STEP_TABS.SETTINGS]: () => (
      <SettingsTab
        node={node}
        processId={processId}
        variables={variables}
        isStart={isStart}
        onChange={onChange}
        onStartHere={onStartHere}
        onDelete={onDelete}
        automations={
          <section className="canvas-workspace__automations">
            <h4 className="canvas-workspace__section-title">
              {WORKSPACE.AUTOMATIONS}
            </h4>
            <AutomationsTab node={node} onChange={onChange} />
          </section>
        }
      />
    ),
  };

  const tabLabels = {
    [STEP_TABS.MAIN]: get(STEP, ["TABS", "MAIN", kind]),
    [STEP_TABS.NEXT]: NEXT.TAB,
    [STEP_TABS.SETTINGS]: STEP.TABS.SETTINGS,
    [STEP_TABS.FIELDS]: t("SCREEN_WORKSHOP.VIEWS.FIELDS"),
  };

  const screenStatus = () => {
    const state = screenState(data);
    const versions = screenVersions(data);
    const published = isNil(versions.published)
      ? null
      : t("CANVAS_EDITOR.STEP.SCREEN.PUBLISHED", {
          version: versions.published,
        });
    if (!isNil(versions.draft))
      return {
        tone: STATUS_TONES.DRAFT,
        text: WORKSPACE.SCREEN_STATUS.DRAFT,
        title: [
          published,
          t("CANVAS_EDITOR.STEP.SCREEN.DRAFT", { version: versions.draft }),
        ]
          .filter(Boolean)
          .join(" · "),
      };
    if (state === SCREEN_STATES.PUBLISHED)
      return {
        tone: STATUS_TONES.OK,
        text: WORKSPACE.SCREEN_STATUS.ONLINE,
        title: published || STEP.SCREEN.PUBLISHED_PLAIN,
      };
    if (state === SCREEN_STATES.SHARED)
      return {
        tone: STATUS_TONES.OK,
        text: WORKSPACE.SCREEN_STATUS.SHARED,
        title: t("CANVAS_EDITOR.STEP.SCREEN.SHARED", {
          name: data.screenFromName,
        }),
      };
    if (state === SCREEN_STATES.UNPUBLISHED)
      return {
        tone: STATUS_TONES.ERROR,
        text: WORKSPACE.SCREEN_STATUS.UNPUBLISHED,
        title: STEP.SCREEN.NOT_PUBLISHED,
      };
    return {
      tone: STATUS_TONES.MUTED,
      text: WORKSPACE.SCREEN_STATUS.NONE,
      title: STEP.SCREEN.NONE,
    };
  };

  const status = isScreen ? screenStatus() : null;
  const subtitle = [
    data.tag
      ? t("CANVAS_EDITOR.WORKSPACE.PROJECT_STATUS", {
          tag: TAGS[data.tag] || data.tag,
        })
      : WORKSPACE.NO_PROJECT_STATUS,
    data.progress
      ? t("CANVAS_EDITOR.WORKSPACE.PROGRESS", { progress: data.progress })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className="canvas-workspace" aria-label={data.name || UNNAMED}>
      {sideMeta
        ? createPortal(
            <span className="canvas-workspace__meta canvas-side__meta">
              <span>{subtitle}</span>
              {status ? (
                <span
                  className={`canvas-workspace__status canvas-workspace__status--${status.tone}`}
                  title={status.title}
                >
                  <span className="canvas-workspace__status-dot" />
                  {status.text}
                </span>
              ) : null}
            </span>,
            sideMeta,
          )
        : null}
      <div className="canvas-workspace__body">
        <div
          className={classNames("canvas-workspace__content", {
            "canvas-workspace__content--wide":
              isScreen && SCREEN_TABS.includes(shown),
          })}
        >
          {isScreen ? (
            <StatesSlotContext.Provider value={statesSlot}>
              <FieldsSlotContext.Provider value={fieldsSlot}>
                <ScreenTab
                  key={node.id}
                  node={node}
                  nodes={nodes}
                  tab={fieldsOpen ? STEP_TABS.FIELDS : shown}
                  persisted={persisted}
                  processId={processId}
                  variables={variables}
                  liveFormDefinition={liveFormDefinition}
                  onChange={onChange}
                  onFieldsOpen={(open) => {
                    if (!open) {
                      onSideOpen(false);
                      return;
                    }
                    onSideTab(STEP_TABS.FIELDS);
                    onSideOpen(true);
                  }}
                  reload={reload}
                />
              </FieldsSlotContext.Provider>
            </StatesSlotContext.Provider>
          ) : null}
          {!isScreen && !isNil(shown) ? tabContent[shown]() : null}
          {isNil(shown) ? (
            <p className="canvas-tab__hint">{WORKSPACE.SIDE_HINT}</p>
          ) : null}
        </div>
      </div>
      {sideBody
        ? createPortal(
            <div className="canvas-side__content">
              {isScreen && SCREEN_TABS.includes(shown) ? (
                <div
                  ref={setStatesSlot}
                  className="canvas-workspace__states canvas-side__states"
                />
              ) : null}
              <div
                className="canvas-workspace__tabs canvas-side__tabs"
                role="tablist"
                aria-label={WORKSPACE.TABS}
              >
                {sideTabs.map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={key === sideShown}
                    className={classNames("canvas-inspector__tab", {
                      "canvas-inspector__tab--active": key === sideShown,
                    })}
                    onClick={() => onSideTab(key)}
                  >
                    {tabLabels[key]}
                    {key === STEP_TABS.FIELDS ? (
                      <span
                        ref={setFieldsBadge}
                        className="canvas-side__badge"
                      />
                    ) : null}
                  </button>
                ))}
              </div>
              <div className="canvas-side__tab">
                {sideShown === STEP_TABS.FIELDS ? (
                  <div ref={setFieldsPanel} className="canvas-side__fields" />
                ) : (
                  tabContent[sideShown]()
                )}
              </div>
            </div>,
            sideBody,
          )
        : null}
    </section>
  );
};

export default StatusPanel;
